import React from 'react';
import classNames from 'classnames';

import { useKitTranslate } from 'common/components/kit-shared/useKitTranslate';
import Button from 'common/components/Button';
import { ButtonSchema, ButtonSize } from 'common/components/Button/constants';
import BottomSheet from 'common/components/kit-shared/BottomSheet';
import styles from './ConfirmDialog.module.scss';

interface IConfirmDialogProps {
  title: string;
  message: string;
  note?: string;
  confirmLabel: string;
  danger?: boolean;
  // Amber notice (warning). Takes precedence over `danger` for the notice tint;
  // `danger` still controls the confirm button color.
  warn?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Centered confirmation dialog with a highlighted notice box and Cancel +
 * confirm buttons. `danger` tints the confirm button (and notice) red; `warn`
 * tints the notice amber.
 */
const ConfirmDialog = ({
  title, message, note, confirmLabel, danger = false, warn = false, busy = false, onConfirm, onCancel,
}: IConfirmDialogProps) => {
  const t = useKitTranslate();

  return (
    <BottomSheet
      title={title}
      onClose={onCancel}
      centered
      closeDisabled={busy}
      footer={(
        <>
          <Button
            schema={ButtonSchema.secondary}
            size={ButtonSize.small}
            onClick={onCancel}
            disabled={busy}
          >
            {t('texts.kits.catalog.cancel')}
          </Button>
          <button
            type="button"
            className={classNames(styles.confirmBtn, { [styles.confirmBtnDanger]: danger })}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </button>
        </>
      )}
    >
      <div className={classNames(styles.notice, {
        [styles.noticeWarn]: warn,
        [styles.noticeDanger]: danger && !warn,
      })}
      >
        <p className={styles.message}>{message}</p>
        {note && <p className={styles.note}>{note}</p>}
      </div>
    </BottomSheet>
  );
};

export default ConfirmDialog;
